"use client";

import { useAppContext } from "@repo/dashboard-core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { fetchRecentEsims } from "@/lib/actions/esim";
import { fetchTopUps } from "@/lib/actions/topup";
import { isEsimHome } from "@/lib/esimCustomer";
import type { RecentEsim } from "@/lib/types/esim";
import type { TopUp } from "@/lib/types/topup";

type Purchases = {
  topUps: TopUp[];
  esims: RecentEsim[];
  /** The eSIM list could not be loaded, as opposed to being empty. */
  esimsFailed: boolean;
  /** Until both lists have loaded once. */
  loading: boolean;
  /** See `isEsimHome`. Until the lists load, the subscription decides. */
  esimHome: boolean;
  /** Loads both lists again; a call while a load is running joins it. */
  reload: () => void;
};

const PurchasesContext = createContext<Purchases | null>(null);

/**
 * The customer's top-ups and eSIMs (GD-135). What they bought decides whether
 * home is the top-up or the eSIM one, and the sidebar and header word it on
 * every page, so the shell loads the lists rather than the home page alone.
 */
export function PurchasesProvider({ children }: { children: React.ReactNode }) {
  const { subscriptionsInfo } = useAppContext();
  const [topUps, setTopUps] = useState<TopUp[]>([]);
  const [esims, setEsims] = useState<RecentEsim[]>([]);
  const [esimsFailed, setEsimsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const loadingNow = useRef(false);

  const reload = useCallback(() => {
    if (loadingNow.current) return;
    loadingNow.current = true;

    void Promise.allSettled([
      fetchTopUps().then((result) => {
        if (result.success) setTopUps(result.data);
      }),
      fetchRecentEsims()
        .then((result) => {
          setEsims(result.success ? result.data : []);
          setEsimsFailed(!result.success);
        })
        // The action itself can reject (connection lost, stale action id after
        // a redeploy); without this the eSIM home would read as empty.
        .catch(() => setEsimsFailed(true)),
    ]).finally(() => {
      loadingNow.current = false;
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const esimHome = isEsimHome({
    subscriptions: subscriptionsInfo?.runningSubscriptions,
    topUps,
    esims,
  });

  const value = useMemo(
    () => ({ topUps, esims, esimsFailed, loading, esimHome, reload }),
    [topUps, esims, esimsFailed, loading, esimHome, reload],
  );

  return (
    <PurchasesContext.Provider value={value}>
      {children}
    </PurchasesContext.Provider>
  );
}

export function usePurchases(): Purchases {
  const purchases = useContext(PurchasesContext);
  if (!purchases) {
    throw new Error("usePurchases must be used within a PurchasesProvider");
  }
  return purchases;
}
